/**
 * Server-sent events for instant payment arrival.
 *
 * SSE is an accelerator, never the source of truth: the five-second poll in
 * `useTransactions` keeps running whatever happens here, so a dropped stream
 * degrades to "up to 5s late" rather than to a dead screen. Reconnection uses
 * capped exponential backoff so a server restart doesn't turn into a hot loop.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { endpoints } from '@/lib/endpoints';
import { queryKeys } from '@/lib/queryKeys';
import type { TraderNotification } from '@/types/api';

export type StreamStatus = 'connecting' | 'live' | 'offline';

const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 30_000;

interface Options {
  token: string | null;
  onPaymentArrived?: (event: TraderNotification) => void;
}

export function useNotificationStream({ token, onPaymentArrived }: Options): StreamStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>('connecting');

  // Kept in a ref so a changing callback identity never rebuilds the stream.
  const arrivedRef = useRef(onPaymentArrived);
  const seenPayments=useRef(new Set<string>());
  arrivedRef.current = onPaymentArrived;

  useEffect(() => {
    if (!token) {
      setStatus('offline');
      return;
    }

    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let cancelled = false;

    const connect = (): void => {
      if (cancelled) return;
      setStatus(attempt === 0 ? 'connecting' : 'offline');

      source = new EventSource(endpoints.notifications(token));

      source.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        setStatus('live');
      };

      source.onmessage = (event: MessageEvent<string>) => {
        if (cancelled) return;

        let payload: TraderNotification;
        try {
          payload = JSON.parse(event.data) as TraderNotification;
        } catch {
          return; // Malformed frame: the poll will reconcile anyway.
        }
        if (payload.type === 'ping') return;

        void queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.balance });
        void queryClient.invalidateQueries({ queryKey: queryKeys.stats });

        if (payload.type === 'kyc.updated') {
          void queryClient.invalidateQueries({ queryKey: queryKeys.kyc });
        }
        if (payload.type === 'payment.pending') {
          if(payload.transactionId){
            if(seenPayments.current.has(payload.transactionId))return;
            seenPayments.current.add(payload.transactionId);
            if(seenPayments.current.size>1000)seenPayments.current.delete(seenPayments.current.values().next().value!);
          }
          arrivedRef.current?.(payload);
        }
      };

      source.onerror = () => {
        if (cancelled) return;
        source?.close();
        source = null;
        setStatus('offline');

        const delay = Math.min(BASE_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      source?.close();
    };
  }, [token, queryClient]);

  return status;
}
